import { z } from "zod";
import { dateOnlySchema, emailSchema, listQuerySchema, phoneSchema, tagsSchema, timestampSchema, uuidSchema } from "./api.ts";
import { createEngagementInputSchema } from "./clients.ts";
import { blankToNull, countryCodeSchema, csvArraySchema, httpUrlSchema, isUnique, queryBooleanSchema, timestampsShape, userRefSchema } from "./common.ts";
import { gstinSchema } from "./india.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";

/**
 * LEADS MODULE CONTRACT — pipelines, stages, lead opportunities, the sales
 * board, pipeline reporting and the lead → client conversion seam (PRD §4.5–4.7,
 * §7 Item 1).
 *   /api/v1/leads, /api/v1/leads/board, /api/v1/leads/stats,
 *   /api/v1/pipelines, /api/v1/pipelines/:id/stages,
 *   /api/v1/lead-sources, /api/v1/lost-reasons, /api/v1/disqualify-reasons
 * Permissions: reads need `leads:read`; writes need `leads:write`; deleting
 * needs `leads:delete`; conversion needs `leads:convert`; pipeline/stage/reason
 * configuration needs `pipeline:manage`.
 *
 * VOCABULARY (PRD §4.1 — do not confuse these):
 *  · the TENANT is an "org" in code and a "Workspace" in the UI,
 *  · a **Lead** is a sales OPPORTUNITY — never a company and never a person,
 *  · a **Contact** is the person the lead is worked through (always required),
 *  · a **Company** is the external business (nullable until the lead qualifies,
 *    required at conversion), and
 *  · a **Client** is the account relationship that conversion creates.
 * Invoices, contracts, engagements and portal access hang off `clientId`, never
 * off a lead (PRD I-2).
 *
 * MONEY VISIBILITY: every `*Minor` field on a lead, a board column or a stats
 * bucket is `null` for a caller without `financials:view` (PRD §7 Item 5 — a
 * team member works the board but sees no money). Counts are never masked.
 */

// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------

/**
 * What a stage means for the leads sitting in it. A lead's `status` IS its
 * stage's category and can never be set independently (PRD I-4), which is why
 * parking, losing and disqualifying all move the lead to a system stage.
 */
export const STAGE_CATEGORIES = ["open", "parked", "won", "lost", "disqualified"] as const;
export type StageCategory = (typeof STAGE_CATEGORIES)[number];
export const stageCategorySchema = z.enum(STAGE_CATEGORIES);

/** Derived from the stage category — the same five values (PRD I-4). */
export const LEAD_STATUSES = STAGE_CATEGORIES;
export type LeadStatus = StageCategory;
export const leadStatusSchema = stageCategorySchema;

/** Categories whose stages are created with the pipeline and can be renamed but never deleted. */
export const SYSTEM_STAGE_CATEGORIES = ["won", "lost", "parked", "disqualified"] as const;

/** Only `open` leads sit on the board and count towards pipeline value. */
export function isOpenCategory(category: StageCategory): boolean {
  return category === "open";
}

/** A lead is either brand-new business or growth inside an existing client (PRD US-1.9). */
export const LEAD_KINDS = ["new_business", "expansion"] as const;
export type LeadKind = (typeof LEAD_KINDS)[number];
export const leadKindSchema = z.enum(LEAD_KINDS);

/** One-off project money, or a monthly retainer (which also reports a TCV). */
export const LEAD_VALUE_TYPES = ["one_time", "recurring"] as const;
export type LeadValueType = (typeof LEAD_VALUE_TYPES)[number];
export const leadValueTypeSchema = z.enum(LEAD_VALUE_TYPES);

export const LEAD_TEMPERATURES = ["hot", "warm", "cold"] as const;
export type LeadTemperature = (typeof LEAD_TEMPERATURES)[number];
export const leadTemperatureSchema = z.enum(LEAD_TEMPERATURES);

/** Default months used for the TCV of a recurring lead that does not say otherwise. */
export const DEFAULT_RECURRING_MONTHS = 12;

/**
 * Fields a stage may demand before a lead is allowed to enter it (PRD US-1.7).
 * The keys are lead DTO field names, so the web form can highlight exactly the
 * input the user has to fill in.
 */
export const LEAD_REQUIRED_FIELDS = [
  "companyId",
  "valueAmountMinor",
  "expectedCloseDate",
  "services",
  "temperature",
  "probability",
  "source",
] as const;
export type LeadRequiredField = (typeof LEAD_REQUIRED_FIELDS)[number];
export const leadRequiredFieldSchema = z.enum(LEAD_REQUIRED_FIELDS);

/**
 * Lead sources seeded into every workspace (PRD §7 US-1.1, India-first).
 * Codes are stable for reporting; the label is editable.
 */
export const DEFAULT_LEAD_SOURCES = [
  { code: "referral", label: "Referral" },
  { code: "instagram", label: "Instagram" },
  { code: "website", label: "Website" },
  { code: "whatsapp", label: "WhatsApp" },
  { code: "indiamart", label: "IndiaMART" },
  { code: "justdial", label: "JustDial" },
  { code: "google", label: "Google / SEO" },
  { code: "linkedin", label: "LinkedIn" },
  { code: "event", label: "Event / networking" },
  { code: "cold_outreach", label: "Cold outreach" },
  { code: "existing_client", label: "Existing client" },
  { code: "other", label: "Other" },
] as const;

/**
 * What a lost reason asks the user to capture as well (PRD §4.6).
 * `competitor` → the competitor's name, `notes` → mandatory notes,
 * `budget` / `service` → a free-text note the report groups on.
 */
export const LOST_REASON_CAPTURES = ["none", "competitor", "budget", "service", "attempts", "notes"] as const;
export type LostReasonCapture = (typeof LOST_REASON_CAPTURES)[number];
export const lostReasonCaptureSchema = z.enum(LOST_REASON_CAPTURES);

/** Lost reasons seeded into every workspace (PRD §4.6). Codes are stable for reporting. */
export const DEFAULT_LOST_REASONS = [
  { code: "PRICE", label: "Budget too low / our price too high", capture: "budget" },
  { code: "COMPETITOR", label: "Chose another agency", capture: "competitor" },
  { code: "FREELANCER", label: "Went with a freelancer", capture: "none" },
  { code: "IN_HOUSE", label: "Decided to do it in-house", capture: "none" },
  { code: "TIMING", label: "Postponed / not now", capture: "none" },
  { code: "NO_RESPONSE", label: "Went silent after proposal/contact", capture: "attempts" },
  { code: "SCOPE_MISMATCH", label: "Needs services we don't offer", capture: "service" },
  { code: "NOT_A_FIT", label: "Too small / wrong industry / values mismatch", capture: "none" },
  { code: "DECISION_CHANGE", label: "Champion left / decision-maker changed", capture: "none" },
  { code: "TERMS", label: "Couldn't agree payment terms or advance", capture: "none" },
  { code: "OTHER", label: "Other", capture: "notes" },
] as const satisfies readonly { code: string; label: string; capture: LostReasonCapture }[];

/** The reason that means "not now" — the UI offers parking instead (PRD US-1.5). */
export const TIMING_LOST_REASON_CODE = "TIMING";

/** Disqualification reasons seeded into every workspace (PRD §4.6). */
export const DEFAULT_DISQUALIFY_REASONS = [
  { code: "SPAM", label: "Spam or bot", requiresLink: false },
  { code: "JOB_SEEKER", label: "Job or internship enquiry", requiresLink: false },
  { code: "VENDOR_PITCH", label: "Someone selling to us", requiresLink: false },
  { code: "DUPLICATE", label: "Duplicate of another lead", requiresLink: true },
  { code: "INFO_ONLY", label: "Student or research, no buying intent", requiresLink: false },
  { code: "INVALID_CONTACT", label: "Unreachable or wrong number", requiresLink: false },
] as const;

/** The reason that must name the lead it duplicates. */
export const DUPLICATE_DISQUALIFY_REASON_CODE = "DUPLICATE";

/**
 * The default "New Business" pipeline (PRD §4.5). The four system stages are
 * created with it: they can be renamed but never deleted, and `parked` /
 * `disqualified` sit off the board.
 */
export const DEFAULT_PIPELINE_NAME = "New Business";
export const DEFAULT_PIPELINE_STAGES = [
  { name: "New", category: "open", probability: 10, staleAfterDays: 1, requiredFields: [] },
  { name: "Contacted", category: "open", probability: 20, staleAfterDays: 5, requiredFields: [] },
  { name: "Qualified", category: "open", probability: 40, staleAfterDays: 7, requiredFields: ["companyId"] },
  { name: "Proposal Sent", category: "open", probability: 60, staleAfterDays: 7, requiredFields: ["valueAmountMinor", "expectedCloseDate"] },
  { name: "Negotiation", category: "open", probability: 80, staleAfterDays: 5, requiredFields: ["valueAmountMinor", "expectedCloseDate"] },
  { name: "Won", category: "won", probability: 100, staleAfterDays: null, requiredFields: [] },
  { name: "Lost", category: "lost", probability: 0, staleAfterDays: null, requiredFields: [] },
  { name: "Parked", category: "parked", probability: 0, staleAfterDays: null, requiredFields: [] },
  { name: "Disqualified", category: "disqualified", probability: 0, staleAfterDays: null, requiredFields: [] },
] as const satisfies readonly { name: string; category: StageCategory; probability: number; staleAfterDays: number | null; requiredFields: readonly LeadRequiredField[] }[];

/** Speed-to-lead SLA: the first response is due this long after capture (PRD US-1.4). */
export const SPEED_TO_LEAD_SLA_MINUTES = 60;
/** A conversion can be reverted for this long, and only by an owner or admin (PRD C-8). */
export const CONVERSION_REVERT_WINDOW_DAYS = 7;
export const MAX_PIPELINES_PER_ORG = 10;
export const MAX_OPEN_STAGES_PER_PIPELINE = 12;
export const MIN_OPEN_STAGES_PER_PIPELINE = 3;

// ---------------------------------------------------------------------------
// Derived money
// ---------------------------------------------------------------------------

/**
 * Total contract value of a lead in minor units: a one-off lead is worth its
 * amount, a retainer is worth the monthly amount × the months it runs
 * (default 12, PRD US-1.3).
 */
export function leadTcvMinor(
  valueAmountMinor: number | null | undefined,
  valueType: LeadValueType,
  recurringMonths: number | null | undefined,
): number {
  const amount = valueAmountMinor ?? 0;
  if (valueType !== "recurring") return amount;
  return amount * (recurringMonths ?? DEFAULT_RECURRING_MONTHS);
}

/** TCV × the stage probability — the "weighted pipeline" every forecast uses. */
export function weightedMinor(tcvMinor: number, probability: number): number {
  return Math.round((tcvMinor * probability) / 100);
}

// ---------------------------------------------------------------------------
// Configuration DTOs
// ---------------------------------------------------------------------------

/** One column of a pipeline. `category` decides the status of every lead inside it (PRD I-4). */
export const stageSchema = z.object({
  id: uuidSchema,
  pipelineId: uuidSchema,
  name: z.string(),
  category: stageCategorySchema,
  probability: z.number().int(),
  /** Days after which a lead sitting here is "stale" (PRD §4.5). Null = never. */
  staleAfterDays: z.number().int().nullable(),
  requiredFields: z.array(leadRequiredFieldSchema),
  position: z.number().int(),
  /** Won / Lost / Parked / Disqualified — renameable, never deletable. */
  isSystem: z.boolean(),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type StageDto = z.infer<typeof stageSchema>;

/** A sales pipeline. A workspace has at most `MAX_PIPELINES_PER_ORG` (PRD US-1.7). */
export const pipelineSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  isDefault: z.boolean(),
  position: z.number().int(),
  /** Live (not archived) stages in board order — empty only on a pipeline with none left. */
  stages: z.array(stageSchema),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type PipelineDto = z.infer<typeof pipelineSchema>;

export const leadSourceSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  label: z.string(),
  position: z.number().int(),
  isActive: z.boolean(),
  isCanonical: z.boolean(),
  ...timestampsShape,
});
export type LeadSourceDto = z.infer<typeof leadSourceSchema>;

export const lostReasonSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  label: z.string(),
  capture: lostReasonCaptureSchema,
  position: z.number().int(),
  isActive: z.boolean(),
  isCanonical: z.boolean(),
  ...timestampsShape,
});
export type LostReasonDto = z.infer<typeof lostReasonSchema>;

export const disqualifyReasonSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  label: z.string(),
  requiresLink: z.boolean(),
  position: z.number().int(),
  isActive: z.boolean(),
  isCanonical: z.boolean(),
  ...timestampsShape,
});
export type DisqualifyReasonDto = z.infer<typeof disqualifyReasonSchema>;

// ---------------------------------------------------------------------------
// Lead DTO
// ---------------------------------------------------------------------------

/** A company or contact as a lead card shows it (resolved through the clients module). */
export const leadPartyRefSchema = z.object({ id: uuidSchema, name: z.string(), subtitle: z.string().nullable() });
export type LeadPartyRef = z.infer<typeof leadPartyRefSchema>;

/** UTM parameters captured by the web form (PRD US-1.2). Replaced as a whole on PATCH. */
export const utmSchema = z.object({
  source: z.string().nullable(),
  medium: z.string().nullable(),
  campaign: z.string().nullable(),
  term: z.string().nullable(),
  content: z.string().nullable(),
});
export type Utm = z.infer<typeof utmSchema>;

/** The banner a converted lead shows (PRD C-5): it is read-only apart from notes. */
export const leadConversionSchema = z.object({
  clientId: uuidSchema,
  clientNumber: z.string(),
  convertedAt: timestampSchema,
  convertedBy: userRefSchema.nullable(),
  /** True while an owner/admin may still revert it (PRD C-8). */
  revertable: z.boolean(),
});
export type LeadConversionDto = z.infer<typeof leadConversionSchema>;

export const leadSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  kind: leadKindSchema,
  title: z.string(),
  /** Derived from the stage category and never writable (PRD I-4). */
  status: leadStatusSchema,
  pipelineId: uuidSchema,
  stageId: uuidSchema,
  stageName: z.string(),
  stageCategory: stageCategorySchema,
  primaryContactId: uuidSchema,
  primaryContact: leadPartyRefSchema.nullable(),
  companyId: uuidSchema.nullable(),
  company: leadPartyRefSchema.nullable(),
  clientId: uuidSchema.nullable(),
  owner: userRefSchema.nullable(),
  ownerId: uuidSchema,
  source: z.string().nullable(),
  sourceDetail: z.string().nullable(),
  referrerContactId: uuidSchema.nullable(),
  utm: utmSchema,
  services: z.array(uuidSchema),
  valueType: leadValueTypeSchema,
  /** Null without `financials:view`. For a retainer this is the MONTHLY amount. */
  valueAmountMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  recurringMonths: z.number().int().nullable(),
  /** Total contract value; null without `financials:view` (PRD US-1.3). */
  tcvMinor: minorSchema.nullable(),
  probability: z.number().int(),
  /** TCV × probability; null without `financials:view`. */
  weightedValueMinor: minorSchema.nullable(),
  expectedCloseDate: dateOnlySchema.nullable(),
  temperature: leadTemperatureSchema.nullable(),
  provisionalIndustryId: uuidSchema.nullable(),
  lostReasonId: uuidSchema.nullable(),
  lostReasonCode: z.string().nullable(),
  lostCompetitor: z.string().nullable(),
  lostNotes: z.string().nullable(),
  revisitOn: dateOnlySchema.nullable(),
  disqualifyReasonId: uuidSchema.nullable(),
  disqualifyReasonCode: z.string().nullable(),
  duplicateOfLeadId: uuidSchema.nullable(),
  wonAt: timestampSchema.nullable(),
  /** Null without `financials:view`. */
  wonValueMinor: minorSchema.nullable(),
  convertedClientId: uuidSchema.nullable(),
  conversion: leadConversionSchema.nullable(),
  stageEnteredAt: timestampSchema,
  /** Days the lead has sat in its current stage — the number the card shows. */
  daysInStage: z.number().int(),
  /** True when the stage's stale threshold has passed (PRD US-1.4). */
  isStale: z.boolean(),
  lastActivityAt: timestampSchema.nullable(),
  firstResponseAt: timestampSchema.nullable(),
  closedAt: timestampSchema.nullable(),
  tags: z.array(z.string()),
  customFields: z.record(z.string(), z.unknown()),
  /** Optimistic concurrency token — send it back on a stage move (PRD US-1.3). */
  version: z.number().int(),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type LeadDto = z.infer<typeof leadSchema>;

/** Compact lead reference other modules embed (the client page's "open opportunities"). */
export const leadSummarySchema = z.object({
  id: uuidSchema,
  number: z.string(),
  title: z.string(),
  status: leadStatusSchema,
  stageName: z.string(),
  kind: leadKindSchema,
  valueAmountMinor: minorSchema.nullable(),
  tcvMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  probability: z.number().int(),
  expectedCloseDate: dateOnlySchema.nullable(),
  ownerId: uuidSchema,
  companyId: uuidSchema.nullable(),
  clientId: uuidSchema.nullable(),
  convertedClientId: uuidSchema.nullable(),
});
export type LeadSummaryDto = z.infer<typeof leadSummarySchema>;

/** One entry of the velocity ledger: when the lead entered a stage and when it left (PRD US-1.3). */
export const leadStageHistorySchema = z.object({
  id: uuidSchema,
  leadId: uuidSchema,
  stageId: uuidSchema,
  stageName: z.string(),
  category: stageCategorySchema,
  fromStageId: uuidSchema.nullable(),
  enteredAt: timestampSchema,
  exitedAt: timestampSchema.nullable(),
  durationSeconds: z.number().int().nullable(),
  actorName: z.string(),
  actorId: uuidSchema.nullable(),
});
export type LeadStageHistoryDto = z.infer<typeof leadStageHistorySchema>;

// ---------------------------------------------------------------------------
// Writable shapes (no defaults — `.partial()` would resurrect them in zod 4)
// ---------------------------------------------------------------------------

const utmInputSchema = z
  .object({
    source: blankToNull(z.string().max(200)).optional(),
    medium: blankToNull(z.string().max(200)).optional(),
    campaign: blankToNull(z.string().max(200)).optional(),
    term: blankToNull(z.string().max(200)).optional(),
    content: blankToNull(z.string().max(200)).optional(),
  })
  .strict();

const leadWritableShape = {
  title: z.string().trim().min(1).max(200),
  primaryContactId: uuidSchema,
  companyId: uuidSchema.nullable(),
  pipelineId: uuidSchema,
  stageId: uuidSchema,
  ownerId: uuidSchema,
  source: blankToNull(z.string().trim().max(60)),
  sourceDetail: blankToNull(z.string().trim().max(200)),
  referrerContactId: uuidSchema.nullable(),
  utm: utmInputSchema,
  services: z.array(uuidSchema).max(30).refine(isUnique, "Duplicate service"),
  valueType: leadValueTypeSchema,
  valueAmountMinor: minorSchema.min(0).nullable(),
  currency: currencyCodeSchema,
  recurringMonths: z.number().int().min(1).max(120).nullable(),
  probability: z.number().int().min(0).max(100),
  expectedCloseDate: dateOnlySchema.nullable(),
  temperature: leadTemperatureSchema.nullable(),
  provisionalIndustryId: uuidSchema.nullable(),
  tags: tagsSchema,
  customFields: z.record(z.string(), z.unknown()),
};

/**
 * POST /api/v1/leads (perm `leads:write`) → 201 Lead.
 * `primaryContactId` is mandatory (PRD I-3): a lead with nobody to call is not a
 * lead. Omitting `pipelineId`/`stageId` drops the lead into the first stage of
 * the default pipeline. `kind: "expansion"` requires `clientId` (PRD US-1.9).
 */
export const createLeadInputSchema = z
  .object({
    ...leadWritableShape,
    kind: leadKindSchema.optional(),
    clientId: uuidSchema.nullable().optional(),
    companyId: leadWritableShape.companyId.optional(),
    pipelineId: leadWritableShape.pipelineId.optional(),
    stageId: leadWritableShape.stageId.optional(),
    ownerId: leadWritableShape.ownerId.optional(),
    source: leadWritableShape.source.optional(),
    sourceDetail: leadWritableShape.sourceDetail.optional(),
    referrerContactId: leadWritableShape.referrerContactId.optional(),
    utm: leadWritableShape.utm.optional(),
    services: leadWritableShape.services.optional(),
    valueType: leadWritableShape.valueType.optional(),
    valueAmountMinor: leadWritableShape.valueAmountMinor.optional(),
    currency: leadWritableShape.currency.optional(),
    recurringMonths: leadWritableShape.recurringMonths.optional(),
    probability: leadWritableShape.probability.optional(),
    expectedCloseDate: leadWritableShape.expectedCloseDate.optional(),
    temperature: leadWritableShape.temperature.optional(),
    provisionalIndustryId: leadWritableShape.provisionalIndustryId.optional(),
    tags: leadWritableShape.tags.optional(),
    customFields: leadWritableShape.customFields.optional(),
  })
  .strict();
export type CreateLeadInput = z.input<typeof createLeadInputSchema>;

/**
 * PATCH /api/v1/leads/:id (perm `leads:write`) → Lead. Merge patch; omitted keys
 * unchanged, `null` clears. `stageId` is NOT accepted here — a stage move goes
 * through PATCH /leads/:id/stage so it carries its version and its history row.
 * A converted lead is read-only (PRD C-5) and answers 409.
 */
export const updateLeadInputSchema = z
  .object({ ...leadWritableShape, version: z.number().int().min(1) })
  .omit({ stageId: true, pipelineId: true })
  .partial()
  .strict();
export type UpdateLeadInput = z.input<typeof updateLeadInputSchema>;

/**
 * POST /api/v1/leads/quick-add (perm `leads:write`) → 201 QuickAddResult.
 * Captures a lead in one call from a phone: the contact is created or matched,
 * the company is optional, and the lead lands in the first stage of the default
 * pipeline (PRD US-1.1). Duplicates answer 409 with `details.matches` unless
 * `createAnyway` is set, which requires a reason and is audited.
 */
export const quickAddLeadInputSchema = z
  .object({
    contactName: z.string().trim().min(1).max(160),
    phone: blankToNull(phoneSchema).optional(),
    email: blankToNull(emailSchema).optional(),
    /** Reuse a person already in the workspace instead of creating one. */
    contactId: uuidSchema.optional(),
    source: z.string().trim().min(1).max(60),
    sourceDetail: blankToNull(z.string().trim().max(200)).optional(),
    title: z.string().trim().min(1).max(200).optional(),
    companyName: blankToNull(z.string().trim().max(200)).optional(),
    companyId: uuidSchema.optional(),
    website: blankToNull(httpUrlSchema).optional(),
    gstin: gstinSchema.optional(),
    city: blankToNull(z.string().trim().max(100)).optional(),
    country: countryCodeSchema.optional(),
    services: z.array(uuidSchema).max(30).optional(),
    valueType: leadValueTypeSchema.optional(),
    valueAmountMinor: minorSchema.min(0).optional(),
    currency: currencyCodeSchema.optional(),
    recurringMonths: z.number().int().min(1).max(120).optional(),
    provisionalIndustryId: uuidSchema.optional(),
    temperature: leadTemperatureSchema.optional(),
    ownerId: uuidSchema.optional(),
    pipelineId: uuidSchema.optional(),
    notes: blankToNull(z.string().trim().max(2000)).optional(),
    tags: tagsSchema.optional(),
    /** "Create anyway" after seeing the duplicate matches — the reason is audited. */
    createAnyway: z.boolean().optional(),
    createAnywayReason: z.string().trim().min(3).max(300).optional(),
  })
  .strict()
  .refine((v) => Boolean(v.phone || v.email || v.contactId), {
    message: "Give a mobile number or an email so the lead can be reached.",
    path: ["phone"],
  })
  .refine((v) => !v.createAnyway || Boolean(v.createAnywayReason), {
    message: "Say why this is not a duplicate.",
    path: ["createAnywayReason"],
  });
export type QuickAddLeadInput = z.input<typeof quickAddLeadInputSchema>;

/** What a duplicate check found, and what the user can do about it (PRD US-1.1). */
export const duplicateMatchSchema = z.object({
  kind: z.enum(["lead", "client", "contact", "company"]),
  id: uuidSchema,
  label: z.string(),
  subtitle: z.string().nullable(),
  /** Why it matched: "phone", "email", "gstin", "name" or "domain". */
  matchedOn: z.string(),
  /** `open_existing`, `new_lead_for_contact` or `expansion_lead` (an active client matched). */
  actions: z.array(z.enum(["open_existing", "new_lead_for_contact", "expansion_lead", "create_anyway"])),
});
export type DuplicateMatch = z.infer<typeof duplicateMatchSchema>;

/** POST /api/v1/leads/duplicate-check (perm `leads:read`) → matches, no writes. */
export const duplicateCheckInputSchema = z
  .object({
    phone: blankToNull(phoneSchema).optional(),
    email: blankToNull(emailSchema).optional(),
    companyName: blankToNull(z.string().trim().max(200)).optional(),
    website: blankToNull(z.string().trim().max(400)).optional(),
    gstin: gstinSchema.optional(),
  })
  .strict();
export type DuplicateCheckInput = z.input<typeof duplicateCheckInputSchema>;

export const duplicateCheckResultSchema = z.object({ matches: z.array(duplicateMatchSchema) });
export type DuplicateCheckResult = z.infer<typeof duplicateCheckResultSchema>;

export const quickAddResultSchema = z.object({
  lead: leadSchema,
  contactId: uuidSchema,
  contactCreated: z.boolean(),
  companyId: uuidSchema.nullable(),
  companyCreated: z.boolean(),
  /** Matches that were shown and overridden, kept for the audit trail. */
  overriddenMatches: z.array(duplicateMatchSchema),
});
export type QuickAddResult = z.infer<typeof quickAddResultSchema>;

// ---------------------------------------------------------------------------
// Lifecycle inputs
// ---------------------------------------------------------------------------

/**
 * PATCH /api/v1/leads/:id/stage (perm `leads:write`) → Lead.
 * `version` is the optimistic concurrency token from the card: a stale version
 * answers 409 with `details.currentVersion` and nothing is overwritten
 * (PRD US-1.3). Won / Lost / Parked / Disqualified stages are refused here —
 * they have their own endpoints so their required capture is enforced (PRD C-1).
 */
export const changeStageInputSchema = z
  .object({ stageId: uuidSchema, version: z.number().int().min(1), note: blankToNull(z.string().trim().max(500)).optional() })
  .strict();
export type ChangeStageInput = z.input<typeof changeStageInputSchema>;

/** POST /api/v1/leads/:id/park (perm `leads:write`) → Lead. A revisit date is mandatory (PRD §4.5). */
export const parkLeadInputSchema = z
  .object({ revisitOn: dateOnlySchema, notes: blankToNull(z.string().trim().max(2000)).optional(), version: z.number().int().min(1).optional() })
  .strict();
export type ParkLeadInput = z.input<typeof parkLeadInputSchema>;

/**
 * POST /api/v1/leads/:id/lost (perm `leads:write`) → Lead.
 * The reason is mandatory (PRD I-5); reasons that capture a competitor or
 * mandatory notes answer 422 without them. Sending `parkInstead` with the
 * TIMING reason parks the lead rather than losing it (PRD US-1.5).
 */
export const markLostInputSchema = z
  .object({
    lostReasonId: uuidSchema,
    competitor: blankToNull(z.string().trim().max(160)).optional(),
    notes: blankToNull(z.string().trim().max(2000)).optional(),
    revisitOn: dateOnlySchema.nullable().optional(),
    parkInstead: z.boolean().optional(),
    version: z.number().int().min(1).optional(),
  })
  .strict();
export type MarkLostInput = z.input<typeof markLostInputSchema>;

/**
 * POST /api/v1/leads/:id/disqualify (perm `leads:write`) → Lead.
 * Disqualified leads are excluded from win-rate denominators (PRD US-1.5).
 */
export const disqualifyLeadInputSchema = z
  .object({
    disqualifyReasonId: uuidSchema,
    notes: blankToNull(z.string().trim().max(2000)).optional(),
    duplicateOfLeadId: uuidSchema.nullable().optional(),
    version: z.number().int().min(1).optional(),
  })
  .strict();
export type DisqualifyLeadInput = z.input<typeof disqualifyLeadInputSchema>;

/**
 * POST /api/v1/leads/:id/reopen (perms `leads:write` + `pipeline:manage`) → Lead.
 * Manager or Owner only. The lead returns to its last open stage (PRD US-1.5).
 */
export const reopenLeadInputSchema = z
  .object({ stageId: uuidSchema.optional(), notes: blankToNull(z.string().trim().max(2000)).optional() })
  .strict();
export type ReopenLeadInput = z.input<typeof reopenLeadInputSchema>;

// ---------------------------------------------------------------------------
// Conversion (PRD §4.7)
// ---------------------------------------------------------------------------

/** The company half of the wizard: pick a match, or describe the one to create. */
const convertCompanyInputSchema = z
  .object({
    companyId: uuidSchema.optional(),
    kind: z.enum(["company", "individual"]).optional(),
    displayName: z.string().trim().min(1).max(200).optional(),
    legalName: blankToNull(z.string().trim().max(200)).optional(),
    gstin: gstinSchema.optional(),
    website: blankToNull(httpUrlSchema).optional(),
    domain: blankToNull(z.string().trim().max(253)).optional(),
    city: blankToNull(z.string().trim().max(100)).optional(),
    stateCode: blankToNull(z.string().trim().max(2)).optional(),
    country: countryCodeSchema.optional(),
    sizeBand: blankToNull(z.string().trim().max(20)).optional(),
    primaryIndustryId: uuidSchema.nullable().optional(),
    subIndustryId: uuidSchema.nullable().optional(),
  })
  .strict();

const convertClientInputSchema = z
  .object({
    displayName: blankToNull(z.string().trim().max(200)).optional(),
    accountManagerId: uuidSchema.optional(),
    backupAmId: uuidSchema.nullable().optional(),
    team: z.array(uuidSchema).max(30).optional(),
    clientSince: dateOnlySchema.optional(),
    portalEnabled: z.boolean().optional(),
    tags: tagsSchema.optional(),
    customFields: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

/**
 * POST /api/v1/leads/:id/convert (perm `leads:convert`) → 201 ConversionResult.
 * REQUIRES an `Idempotency-Key` header (PRD C-2, C-10): a replay returns the
 * client that was already created rather than a second one. One SERIALIZABLE
 * transaction does everything or nothing (PRD C-7).
 *   · the company already has an ACTIVE client → no new client, the win is an
 *     expansion and `expansion: true` comes back (PRD C-3),
 *   · the company's client has CHURNED → 409 `details.action = "reactivate"`
 *     unless `reactivate: true` is sent, then it goes churned → onboarding with
 *     `clientSince` unchanged (PRD C-4).
 */
export const convertLeadInputSchema = z
  .object({
    company: convertCompanyInputSchema.optional(),
    /** Defaults to the lead's primary contact. */
    primaryContactId: uuidSchema.optional(),
    additionalContacts: z
      .array(
        z
          .object({
            contactId: uuidSchema,
            roles: z.array(z.enum(["decision_maker", "day_to_day", "approver", "billing", "other"])).max(5).optional(),
            title: blankToNull(z.string().trim().max(120)).optional(),
            isBilling: z.boolean().optional(),
          })
          .strict(),
      )
      .max(20)
      .optional(),
    client: convertClientInputSchema.optional(),
    /** The optional first engagement, created in the same transaction (PRD §4.7 step 4). */
    engagement: createEngagementInputSchema.optional(),
    wonValueMinor: minorSchema.min(0).optional(),
    /** Confirms the win-back of a churned account (PRD C-4). */
    reactivate: z.boolean().optional(),
    note: blankToNull(z.string().trim().max(2000)).optional(),
  })
  .strict();
export type ConvertLeadInput = z.input<typeof convertLeadInputSchema>;

export const convertLeadResultSchema = z.object({
  lead: leadSchema,
  clientId: uuidSchema,
  clientNumber: z.string(),
  companyId: uuidSchema,
  companyCreated: z.boolean(),
  contactId: uuidSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  /** False when the win attached to an account that already existed (PRD C-3). */
  clientCreated: z.boolean(),
  /** True when a churned account was won back (PRD C-4). */
  reactivated: z.boolean(),
  /** True when no new client was created because the company already had one. */
  expansion: z.boolean(),
  /** True when this call replayed an earlier conversion instead of doing one. */
  replayed: z.boolean(),
});
export type ConvertLeadResult = z.infer<typeof convertLeadResultSchema>;

/** A company the wizard offers as a match, with the account it already has (PRD §4.7 step 1). */
export const companyMatchSchema = z.object({
  id: uuidSchema,
  displayName: z.string(),
  legalName: z.string().nullable(),
  city: z.string().nullable(),
  domain: z.string().nullable(),
  clientId: uuidSchema.nullable(),
  clientNumber: z.string().nullable(),
  clientStatus: z.string().nullable(),
});
export type CompanyMatch = z.infer<typeof companyMatchSchema>;

/** GET /api/v1/leads/:id/convert-preview (perm `leads:convert`) → what the wizard pre-fills. */
export const convertPreviewSchema = z.object({
  leadId: uuidSchema,
  /** Blockers the user must clear before the wizard can be confirmed (PRD C-1). */
  blockers: z.array(z.string()),
  companyMatches: z.array(companyMatchSchema),
  /** Set when the lead already points at a company or an expansion client. */
  existingClientId: uuidSchema.nullable(),
  existingClientStatus: z.string().nullable(),
  suggestedAccountManagerId: uuidSchema.nullable(),
  suggestedWonValueMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
});
export type ConvertPreview = z.infer<typeof convertPreviewSchema>;

/**
 * POST /api/v1/leads/:id/revert-conversion (perm `leads:convert` + `org:manage`)
 * → Lead. Owner/Admin only, inside `CONVERSION_REVERT_WINDOW_DAYS`, and refused
 * with 409 + `details.blockers` when the client has moved on (PRD C-8).
 */
export const revertConversionInputSchema = z.object({ reason: z.string().trim().min(3).max(300) }).strict();
export type RevertConversionInput = z.input<typeof revertConversionInputSchema>;

/**
 * The lead comes back open in the same transaction. The ACCOUNT is archived by
 * the clients module, which owns that decision and its own blockers — it reacts
 * to the `lead.conversion_reverted` event this call emits.
 */
export const revertConversionResultSchema = z.object({
  lead: leadSchema,
  clientId: uuidSchema,
  clientAlreadyArchived: z.boolean(),
});
export type RevertConversionResult = z.infer<typeof revertConversionResultSchema>;

// ---------------------------------------------------------------------------
// Lists, board and stats
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/leads (perm `leads:read`). Every filter of PRD US-1.3 is here;
 * `noNextStep` surfaces the leads nobody has scheduled a follow-up for (US-1.4).
 */
export const listLeadsQuerySchema = listQuerySchema.extend({
  status: csvArraySchema(leadStatusSchema, LEAD_STATUSES.length).optional(),
  pipelineId: uuidSchema.optional(),
  stageId: csvArraySchema(uuidSchema, 20).optional(),
  ownerId: csvArraySchema(uuidSchema, 50).optional(),
  source: csvArraySchema(z.string().max(60), 30).optional(),
  serviceId: csvArraySchema(uuidSchema, 30).optional(),
  industryId: csvArraySchema(uuidSchema, 30).optional(),
  companyId: uuidSchema.optional(),
  clientId: uuidSchema.optional(),
  contactId: uuidSchema.optional(),
  kind: leadKindSchema.optional(),
  temperature: csvArraySchema(leadTemperatureSchema, 3).optional(),
  tag: csvArraySchema(z.string().max(40), 20).optional(),
  minValueMinor: z.coerce.number().int().min(0).optional(),
  maxValueMinor: z.coerce.number().int().min(0).optional(),
  expectedCloseFrom: dateOnlySchema.optional(),
  expectedCloseTo: dateOnlySchema.optional(),
  createdFrom: dateOnlySchema.optional(),
  createdTo: dateOnlySchema.optional(),
  /** Leads with no activity since this date — the "going cold" filter. */
  lastActivityBefore: dateOnlySchema.optional(),
  /** Only leads sitting past their stage's stale threshold. */
  stale: queryBooleanSchema.optional(),
  /** Only leads with no scheduled follow-up (PRD US-1.4). */
  noNextStep: queryBooleanSchema.optional(),
  includeArchived: queryBooleanSchema.optional(),
  sort: z.enum(["createdAt", "updatedAt", "expectedCloseDate", "valueAmountMinor", "stageEnteredAt", "title", "number"]).optional(),
});
export type ListLeadsQuery = z.infer<typeof listLeadsQuerySchema>;

/** GET /api/v1/leads/board (perm `leads:read`) → one column per board stage. */
export const leadBoardQuerySchema = z
  .object({
    pipelineId: uuidSchema.optional(),
    ownerId: csvArraySchema(uuidSchema, 50).optional(),
    source: csvArraySchema(z.string().max(60), 30).optional(),
    temperature: csvArraySchema(leadTemperatureSchema, 3).optional(),
    serviceId: csvArraySchema(uuidSchema, 30).optional(),
    q: z.string().trim().max(200).optional(),
    /** Cards returned per column; the counts and totals always cover every lead. */
    cardsPerStage: z.coerce.number().int().min(0).max(100).default(25),
  })
  .strict();
export type LeadBoardQuery = z.infer<typeof leadBoardQuerySchema>;

export const boardColumnSchema = z.object({
  stage: stageSchema,
  count: z.number().int(),
  /** Sum of TCV in the workspace base currency; null without `financials:view`. */
  totalValueMinor: minorSchema.nullable(),
  /** Sum of monthly value for recurring leads; null without `financials:view`. */
  monthlyValueMinor: minorSchema.nullable(),
  weightedValueMinor: minorSchema.nullable(),
  /** Leads priced in another currency — deliberately NOT converted (no implicit FX). */
  otherCurrencyCount: z.number().int(),
  leads: z.array(leadSchema),
  hasMore: z.boolean(),
});
export type BoardColumn = z.infer<typeof boardColumnSchema>;

export const leadBoardSchema = z.object({
  pipelineId: uuidSchema,
  pipelineName: z.string(),
  currency: currencyCodeSchema,
  columns: z.array(boardColumnSchema),
});
export type LeadBoard = z.infer<typeof leadBoardSchema>;

/** GET /api/v1/leads/stats (perm `leads:read` + `reports:view`). Periods are IST-bounded dates. */
export const leadStatsQuerySchema = z
  .object({
    pipelineId: uuidSchema.optional(),
    ownerId: csvArraySchema(uuidSchema, 50).optional(),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
  })
  .strict();
export type LeadStatsQuery = z.infer<typeof leadStatsQuerySchema>;

/** One row of a breakdown: an id/label plus its count and money (money null without `financials:view`). */
export const statsBucketSchema = z.object({
  key: z.string().nullable(),
  label: z.string(),
  count: z.number().int(),
  totalValueMinor: minorSchema.nullable(),
  weightedValueMinor: minorSchema.nullable(),
});
export type StatsBucket = z.infer<typeof statsBucketSchema>;

export const funnelStepSchema = z.object({
  stageId: uuidSchema,
  stageName: z.string(),
  position: z.number().int(),
  /** Leads that ever entered this stage in the period. */
  entered: z.number().int(),
  /** …of which reached any later stage. */
  advanced: z.number().int(),
  conversionRate: z.number(),
});
export type FunnelStep = z.infer<typeof funnelStepSchema>;

export const leadStatsSchema = z.object({
  from: dateOnlySchema.nullable(),
  to: dateOnlySchema.nullable(),
  currency: currencyCodeSchema,
  /** Open pipeline right now (not period-bounded — a pipeline is a snapshot). */
  openCount: z.number().int(),
  pipelineValueMinor: minorSchema.nullable(),
  weightedPipelineValueMinor: minorSchema.nullable(),
  byStage: z.array(statsBucketSchema),
  byOwner: z.array(statsBucketSchema),
  bySource: z.array(statsBucketSchema),
  byIndustry: z.array(statsBucketSchema),
  byService: z.array(statsBucketSchema),
  /** Closed in the period. Disqualified leads are excluded from the win rate (PRD US-1.5). */
  wonCount: z.number().int(),
  lostCount: z.number().int(),
  disqualifiedCount: z.number().int(),
  winRate: z.number().nullable(),
  wonValueMinor: minorSchema.nullable(),
  averageDealSizeOneTimeMinor: minorSchema.nullable(),
  averageDealSizeRecurringMonthlyMinor: minorSchema.nullable(),
  medianSalesCycleDays: z.number().nullable(),
  medianSpeedToLeadMinutes: z.number().nullable(),
  funnel: z.array(funnelStepSchema),
  lostReasons: z.array(statsBucketSchema),
  wonValueBySource: z.array(statsBucketSchema),
  newThisWeek: z.number().int(),
  noNextStepCount: z.number().int(),
  staleCount: z.number().int(),
});
export type LeadStats = z.infer<typeof leadStatsSchema>;

// ---------------------------------------------------------------------------
// Configuration inputs
// ---------------------------------------------------------------------------

const pipelineWritableShape = {
  name: z.string().trim().min(1).max(80),
  description: blankToNull(z.string().trim().max(500)),
  isDefault: z.boolean(),
  position: z.number().int().min(0).max(100),
};

/** POST /api/v1/pipelines (perm `pipeline:manage`) → 201 Pipeline. Seeds the four system stages. */
export const createPipelineInputSchema = z
  .object({
    ...pipelineWritableShape,
    description: pipelineWritableShape.description.optional(),
    isDefault: pipelineWritableShape.isDefault.optional(),
    position: pipelineWritableShape.position.optional(),
    /** Open stages to create with it; the system stages are always added. */
    stages: z
      .array(
        z
          .object({
            name: z.string().trim().min(1).max(60),
            probability: z.number().int().min(0).max(100).optional(),
            staleAfterDays: z.number().int().min(0).max(365).nullable().optional(),
            requiredFields: z.array(leadRequiredFieldSchema).max(LEAD_REQUIRED_FIELDS.length).optional(),
          })
          .strict(),
      )
      .min(MIN_OPEN_STAGES_PER_PIPELINE)
      .max(MAX_OPEN_STAGES_PER_PIPELINE)
      .optional(),
  })
  .strict();
export type CreatePipelineInput = z.input<typeof createPipelineInputSchema>;

/** PATCH /api/v1/pipelines/:id (perm `pipeline:manage`) → Pipeline. */
export const updatePipelineInputSchema = z.object(pipelineWritableShape).partial().strict();
export type UpdatePipelineInput = z.input<typeof updatePipelineInputSchema>;

const stageWritableShape = {
  name: z.string().trim().min(1).max(60),
  probability: z.number().int().min(0).max(100),
  staleAfterDays: z.number().int().min(0).max(365).nullable(),
  requiredFields: z.array(leadRequiredFieldSchema).max(LEAD_REQUIRED_FIELDS.length),
  position: z.number().int().min(0).max(100),
};

/**
 * POST /api/v1/pipelines/:id/stages (perm `pipeline:manage`) → 201 Stage.
 * Only `open` stages can be added: Won, Lost, Parked and Disqualified come with
 * the pipeline (PRD US-1.7).
 */
export const createStageInputSchema = z
  .object({
    ...stageWritableShape,
    probability: stageWritableShape.probability.optional(),
    staleAfterDays: stageWritableShape.staleAfterDays.optional(),
    requiredFields: stageWritableShape.requiredFields.optional(),
    position: stageWritableShape.position.optional(),
  })
  .strict();
export type CreateStageInput = z.input<typeof createStageInputSchema>;

/** PATCH /api/v1/stages/:id (perm `pipeline:manage`) → Stage. A system stage accepts only `name`. */
export const updateStageInputSchema = z.object(stageWritableShape).partial().strict();
export type UpdateStageInput = z.input<typeof updateStageInputSchema>;

/**
 * DELETE /api/v1/stages/:id (perm `pipeline:manage`) → 204.
 * A stage holding leads needs `?destinationStageId=` to move them to (PRD US-1.7).
 */
export const deleteStageQuerySchema = z.object({ destinationStageId: uuidSchema.optional() }).strict();
export type DeleteStageQuery = z.infer<typeof deleteStageQuerySchema>;

const reasonWritableShape = {
  label: z.string().trim().min(1).max(160),
  position: z.number().int().min(0).max(200),
  isActive: z.boolean(),
};

/** POST /api/v1/lost-reasons (perm `pipeline:manage`) → 201. Codes are immutable once created. */
export const createLostReasonInputSchema = z
  .object({
    ...reasonWritableShape,
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z][A-Z0-9_]{1,39}$/, "Use an UPPER_SNAKE code"),
    capture: lostReasonCaptureSchema.optional(),
    position: reasonWritableShape.position.optional(),
    isActive: reasonWritableShape.isActive.optional(),
  })
  .strict();
export type CreateLostReasonInput = z.input<typeof createLostReasonInputSchema>;

export const updateLostReasonInputSchema = z.object({ ...reasonWritableShape, capture: lostReasonCaptureSchema }).partial().strict();
export type UpdateLostReasonInput = z.input<typeof updateLostReasonInputSchema>;

/** POST /api/v1/disqualify-reasons (perm `pipeline:manage`) → 201. */
export const createDisqualifyReasonInputSchema = z
  .object({
    ...reasonWritableShape,
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z][A-Z0-9_]{1,39}$/, "Use an UPPER_SNAKE code"),
    requiresLink: z.boolean().optional(),
    position: reasonWritableShape.position.optional(),
    isActive: reasonWritableShape.isActive.optional(),
  })
  .strict();
export type CreateDisqualifyReasonInput = z.input<typeof createDisqualifyReasonInputSchema>;

export const updateDisqualifyReasonInputSchema = z.object({ ...reasonWritableShape, requiresLink: z.boolean() }).partial().strict();
export type UpdateDisqualifyReasonInput = z.input<typeof updateDisqualifyReasonInputSchema>;

/** POST /api/v1/lead-sources (perm `pipeline:manage`) → 201. */
export const createLeadSourceInputSchema = z
  .object({
    ...reasonWritableShape,
    code: z
      .string()
      .trim()
      .toLowerCase()
      .regex(/^[a-z][a-z0-9_]{1,39}$/, "Use a lower_snake code"),
    position: reasonWritableShape.position.optional(),
    isActive: reasonWritableShape.isActive.optional(),
  })
  .strict();
export type CreateLeadSourceInput = z.input<typeof createLeadSourceInputSchema>;

export const updateLeadSourceInputSchema = z.object(reasonWritableShape).partial().strict();
export type UpdateLeadSourceInput = z.input<typeof updateLeadSourceInputSchema>;

export const listPipelinesQuerySchema = z.object({ includeArchived: queryBooleanSchema.optional() }).strict();
export type ListPipelinesQuery = z.infer<typeof listPipelinesQuerySchema>;

export const listReasonsQuerySchema = z.object({ includeInactive: queryBooleanSchema.optional() }).strict();
export type ListReasonsQuery = z.infer<typeof listReasonsQuerySchema>;
